/**
 * useTutorLiveChromeChatResume — le fil ouvert du chat du masteur et les notifications « messages », comme
 * l'ancien chat (`common/messenger/CommonChat.js:173-190`, `:226-260`), tant que le chat est ouvert sur un fil :
 *  - à l'ouverture d'un fil, les notifications « messages » déjà affichées s'effacent (`clearNotifications`) ;
 *  - une notification « messages » de ce fil, reçue pendant qu'il est ouvert, s'efface 1 s après son arrivée ;
 *  - au retour dans l'app (`AppState` : de `inactive` ou `background` à `active`), les notifications
 *    « messages » s'effacent et le fil se relit (`messengerService.getMessages {threadId}`, par la relecture
 *    du chat) : un message arrivé pendant que l'app dormait entre dans le fil.
 *
 * Les notifications sont celles du téléphone : sur le web, seule la relecture part (l'ancien gardait aussi
 * `Platform.OS !== "web"`). L'effacement est celui de la messagerie de l'élève (`clearMessageNotifications`).
 */
import * as Notifications from "expo-notifications"
import { useEffect, useRef } from "react"
import { AppState, Platform } from "react-native"

import { type EngineTelemetry } from "../../../../../../engine/shared/telemetry"
import { MESSAGE_NOTIFICATION_PATH, clearMessageNotifications } from "../../../hooks/messageNotifications"

/** Le délai de l'ancien chat avant d'effacer la notification du fil ouvert. */
export const OPEN_THREAD_NOTIFICATION_DISMISS_MS = 1000

type NotificationData = { path?: unknown; data?: { thread_id?: unknown } | null } | null | undefined

export function useTutorLiveChromeChatResume(args: {
    /** Le chat est ouvert. */
    open: boolean
    /** Le fil ouvert ; `null` : rien à relire. */
    threadId: string | null
    /** La relecture du fil (`getMessages`), celle du chat. */
    refresh: (threadId: string) => Promise<unknown>
    telemetry: EngineTelemetry
}): void {
    const { open, threadId } = args
    const latest = useRef(args)
    latest.current = args

    useEffect(() => {
        if (!open || threadId == null) return undefined
        clearMessageNotifications()

        // Le minuteur survit à la fermeture du fil, comme l'ancien : la notification arrivée part quand même.
        const received =
            Platform.OS !== "web"
                ? Notifications.addNotificationReceivedListener((notification) => {
                      const data = notification.request.content.data as NotificationData
                      if (data?.path !== MESSAGE_NOTIFICATION_PATH || String(data?.data?.thread_id) !== threadId) return
                      setTimeout(() => {
                          Notifications.dismissNotificationAsync(notification.request.identifier).catch(() => {})
                      }, OPEN_THREAD_NOTIFICATION_DISMISS_MS)
                  })
                : null

        const appState = { current: AppState.currentState as string }
        const subscription = AppState.addEventListener("change", (next) => {
            if (/inactive|background/.test(appState.current) && next === "active") {
                clearMessageNotifications()
                const { refresh, telemetry } = latest.current
                refresh(threadId).catch((cause: unknown) => telemetry.error("chat_messages", cause))
            }
            appState.current = next
        })

        return () => {
            received?.remove()
            subscription.remove()
        }
    }, [open, threadId])
}
